import { GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { ColDef } from 'ag-grid-community'
import { Button } from 'antd'
import React from 'react'

import type { UploadStatusNormalized } from './utils/uploadStatus'

interface StatusMessageGridProps {
  setStatusView: (view: string) => void
  statusView: string
  statusResponse: UploadStatusNormalized
}

export function StatusMessageGrid({
  setStatusView,
  statusView,
  statusResponse,
}: StatusMessageGridProps) {
  if (!statusResponse) return null

  const sr = statusResponse as Record<string, unknown>
  const errorCount = typeof sr.error_count === 'number' ? sr.error_count : 0
  const warningCount =
    typeof sr.warning_count === 'number' ? sr.warning_count : 0
  const hasErrors = errorCount > 0
  const hasWarnings = warningCount > 0

  if (!hasErrors && !hasWarnings) return null

  const columnDefs: ColDef[] = [
    {
      headerName: 'Row',
      field: 'row',
      width: 60,
      flex: 0,
      sortable: true,
    },
    {
      headerName: 'Field',
      field: 'field',
      width: 140,
      flex: 0,
      sortable: true,
    },
    {
      headerName: 'Message',
      field: 'message',
      flex: 1,
      sortable: true,
    },
    {
      headerName: 'Type',
      field: 'type',
      width: 80,
      flex: 0,
      sortable: true,
      cellRenderer: (params: { value: unknown }) => {
        const type = String(params.value ?? '')
        return (
          <span
            style={{
              color: type === 'error' ? '#ff4d4f' : '#faad14',
              fontWeight: 'bold',
            }}
          >
            {type.toUpperCase()}
          </span>
        )
      },
    },
  ]

  interface StatusRow {
    row: number
    field: string
    message: string
    type: 'error' | 'warning'
  }

  const getRowData = () => {
    const data: StatusRow[] = []

    const errors = Array.isArray(statusResponse.errors)
      ? (statusResponse.errors as unknown[])
      : []
    if (errors.length > 0) {
      errors.forEach((error) => {
        const e = error as Record<string, unknown>
        data.push({
          row: e.index,
          field: typeof e.field === 'string' ? e.field : 'Unknown',
          message: typeof e.message === 'string' ? e.message : 'Unknown error',
          type: 'error',
        })
      })
    }

    const warnings = Array.isArray(statusResponse.warnings)
      ? (statusResponse.warnings as unknown[])
      : []
    if (warnings.length > 0) {
      warnings.forEach((warning, index: number) => {
        const w = warning as Record<string, unknown>
        data.push({
          row: typeof w.row === 'number' ? w.row : index + 1,
          field: typeof w.field === 'string' ? w.field : 'Unknown',
          message:
            typeof w.message === 'string' ? w.message : 'Unknown warning',
          type: 'warning',
        })
      })
    }

    return data
  }

  const filteredData = getRowData().filter((item) => {
    if (statusView === 'error') return item.type === 'error'
    if (statusView === 'warning') return item.type === 'warning'
    return true
  })

  return (
    <Vertical gap={16}>
      <Horizontal gap={8} alignItems='center'>
        <Button
          type={statusView === '' ? 'primary' : 'default'}
          onClick={() => setStatusView('')}
        >
          All Issues ({getRowData().length})
        </Button>
        {hasErrors && (
          <Button
            type={statusView === 'error' ? 'primary' : 'default'}
            onClick={() => setStatusView('error')}
          >
            Errors ({errorCount})
          </Button>
        )}
        {hasWarnings && (
          <Button
            type={statusView === 'warning' ? 'primary' : 'default'}
            onClick={() => setStatusView('warning')}
          >
            Warnings ({warningCount})
          </Button>
        )}
      </Horizontal>

      <GraviGrid
        rowData={filteredData}
        agPropOverrides={{
          columnDefs,
          defaultColDef: {
            resizable: true,
            sortable: true,
          },
          domLayout: 'autoHeight',
          animateRows: true,
          getRowId: (params) => params.data.row,
        }}
        height='300px'
      />
    </Vertical>
  )
}
